<template>
    <div class="container">
        <section class="section">
            <div class="container" style="max-width: 420px">
                <h2 class="title is-4">Reserve Keyword</h2>

                <div v-if="competition" class="subtitle is-6 mb-4">
                    {{ competition.name }}
                    <div v-if="!competition.isCurrent" class="notification is-warning mt-2">
                        This is not the current competition. Please check the latest competition.
                    </div>
                </div>

                <div class="field">
                    <label class="label has-text-left">Keyword <span class="has-text-danger">*</span></label>
                    <div class="control">
                        <input :class="['input', { 'is-danger': showError }]"
                               type="text"
                               v-model="keyword"
                               placeholder="Enter your keyword"
                               @input="clearMessages">
                    </div>
                    <p v-if="showError" class="help is-danger">
                        Keyword is required
                    </p>
                </div>

                <div class="field button-container">
                    <div class="buttons is-right">
                        <button v-if="isLoggedIn"
                                class="button is-link"
                                @click="goToCompetition">
                            Go to competition
                        </button>
                        <button class="button is-primary"
                                @click="handleReserve"
                                :disabled="isLoading">
                            <span v-if="!isLoading">Reserve</span>
                            <span v-else>Reserving...</span>
                        </button>
                    </div>
                </div>

                <div v-if="competition && !competition.isOpen" class="notification is-warning">
                    This competition is currently closed.
                </div>

                <!-- Success Message -->
                <div v-if="successMessage" class="notification is-success">
                    {{ successMessage }}
                </div>

                <!-- Error Message -->
                <div v-if="errorMessage" class="notification is-danger">
                    {{ errorMessage }}
                </div>
            </div>
        </section>
    </div>
</template>

<script>
    import apiService from './../services/apiService';

    export default {
        name: 'reserve-keyword',
        props: {
            competitionId: {
                type: String,
                required: true
            },
            voteKey: {
                type: String,
                required: true
            },
            visitorId: {
                type: String,
                required: true
            },
            isLoggedIn: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                keyword: '',
                showError: false,
                isLoading: false,
                successMessage: '',
                errorMessage: '',
                competition: null
            };
        },
        async created() {
            try {
                const response = await apiService.getCompetition(this.competitionId);
                this.competition = response.data;
            } catch (error) {
                this.errorMessage = (error.response && error.response.data && error.response.data.message)
                    ? error.response.data.message
                    : 'Failed to load competition details.';
            }
        },
        methods: {
            clearMessages() {
                this.successMessage = '';
                this.errorMessage = '';
                this.showError = false;
            },
            setVoteKeyLocalStorage(voteKey) {
                const voteKeyLocalStorageKey = `bosspour_voteKey_${this.competitionId}`;
                localStorage.setItem(voteKeyLocalStorageKey, voteKey);
            },
            setKeyWordLocalStorage(keyword) {
                const keywordLocalStorageKey = `bosspour_keyword_${this.competitionId}`;
                localStorage.setItem(keywordLocalStorageKey, keyword);
            },
            setIsBrewerLocalStorage(isBrewer) {
                const isBrewerLocalStorageKey = `bosspour_isBrewer_${this.competitionId}`;
                localStorage.setItem(isBrewerLocalStorageKey, isBrewer ? 'true' : 'false');
            },
            async handleReserve() {
                // Reset messages
                this.clearMessages();

                // Validate
                if (!this.keyword.trim()) {
                    this.showError = true;
                    return;
                }

                this.isLoading = true;

                try {
                    // Refresh competition details before proceeding
                    const response = await apiService.getCompetition(this.competitionId);
                    this.competition = response.data;

                    // Check competition status
                    if (!this.competition.isOpen) {
                        throw new Error('This competition is currently closed.');
                    }

                    if (!this.competition.isCurrent) {
                        throw new Error('This is not the current competition.');
                    }

                    const payload = {
                        competitionId: this.competitionId,
                        keyword: this.keyword.trim(),
                        voteKey: this.voteKey,
                        visitorId: this.visitorId
                    };

                    const reserveResponse = await apiService.reserveKeyword(payload);
                    const isBrewer = !!(reserveResponse.data && reserveResponse.data.isBrewer === true);
                    this.setVoteKeyLocalStorage(this.voteKey);
                    this.setKeyWordLocalStorage(this.keyword.trim());
                    this.setIsBrewerLocalStorage(isBrewer);
                    this.isLoggedIn = true;
                    this.successMessage = 'Keyword reserved successfully! You are now logged in';
                    
                    this.keyword = ''; // Clear the input after success
                } catch (error) {
                    this.errorMessage = (error.response && error.response.data && error.response.data.message)
                        ? error.response.data.message
                        : 'Failed to reserve keyword. Please try again.';
                } finally {
                    this.isLoading = false;
                }
            },
            goToCompetition() {
                this.$router.push({
                    name: 'current',
                })
            }
        }
    };
</script>

<style scoped>
    .notification {
        margin-top: 1rem;
    }

    .button.is-primary {
        background-color: #494949;
        color: #ffffff;
        border-color: #494949;
    }

    .button.is-primary:hover {
        background-color: #3c3c3c;
        border-color: #3c3c3c;
    }

    .button.is-primary[disabled] {
        background-color: #494949;
        opacity: 0.5;
    }

    .button.is-link {
        background-color: #167df0;
    }

    .has-text-left {
        text-align: left !important;
        display: block;
    }

    .field.is-grouped {
        display: flex;
        gap: 1rem;
    }
</style>